import React, { useState, useEffect, useCallback } from 'react';
import { purchaseAttempt, signup } from '../services/api';
import '../styles/AdminDashboard.css';

const TABS = ['Overview', 'Signups', 'Candy', 'Apparel', 'Clicks'];

const PAGE_LABELS = {
  home: 'Home',
  candy: 'Candy',
  apparel: 'Apparel',
  about: 'About',
  'the-product': 'The Product',
  contact: 'Contact',
  community: 'Community'
};

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [activeTab, setActiveTab] = useState('Overview');

  const [stats, setStats] = useState({
    performanceCandiesClicks: 0,
    athleticWearClicks: 0,
    totalClicks: 0,
    byPage: {},
    totalSignups: 0,
    generatedAt: null
  });
  const [purchaseStats, setPurchaseStats] = useState({
    totalAttempts: 0,
    recentAttempts: 0,
    byProduct: {},
    candyBreakdown: {
      '60g': { single: 0, '3': 0, '6': 0, '12': 0, '24': 0 },
      '30g': { single: 0, '3': 0, '6': 0, '12': 0, '24': 0 }
    },
    apparelBreakdown: [],
    apparelByColor: {},
    apparelBySize: {}
  });
  const [signupStats, setSignupStats] = useState({ total: 0, candy: 0, apparel: 0, 'candy & apparel': 0 });
  const [signupList, setSignupList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* ── Fetch all data ── */
  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'https://sambas-energy-backend.onrender.com/api';
      const response = await fetch(`${apiUrl}/export-summary`);
      const result = await response.json();

      if (result.success) {
        setStats(result.data);
        setError(null);
      } else {
        setError(result.message);
      }

      try {
        const purchaseData = await purchaseAttempt.getStats();
        if (purchaseData.success) setPurchaseStats(purchaseData.data);
      } catch (e) { console.error('Purchase stats error:', e); }

      try {
        const signupData = await signup.getStats();
        if (signupData.success) setSignupStats(signupData.data);
      } catch (e) { console.error('Signup stats error:', e); }

      try {
        const listData = await signup.getAll();
        if (listData.success) setSignupList(listData.data.signups || []);
      } catch (e) { console.error('Signup list error:', e); }

    } catch (err) {
      setError('Failed to fetch data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  /* ── Auto-refresh ── */
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchStats]);

  /* ── Login ── */
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === 'sambas123') {
      setIsAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password');
    }
  };

  /* ══════════════════════════
     LOGIN SCREEN
     ══════════════════════════ */
  if (!isAuthenticated) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-card">
          <h1>Sambas Admin</h1>
          <p>Enter password to continue</p>
          <form onSubmit={handleLogin}>
            <div className="login-input-group">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                autoFocus
              />
            </div>
            <button type="submit" className="login-btn">Log In</button>
            {passwordError && <p className="login-error">{passwordError}</p>}
          </form>
        </div>
      </div>
    );
  }

  /* ── Helper: candy totals ── */
  const total60g = Object.values(purchaseStats.candyBreakdown?.['60g'] || {}).reduce((a, b) => a + b, 0);
  const total30g = Object.values(purchaseStats.candyBreakdown?.['30g'] || {}).reduce((a, b) => a + b, 0);

  /* ══════════════════════════
     DASHBOARD
     ══════════════════════════ */
  return (
    <div className="admin-dashboard-container">

      {/* ── Header ── */}
      <div className="dash-header">
        <div className="dash-header-left">
          <h1>Dashboard</h1>
          <span>
            Last updated:{' '}
            {stats.generatedAt ? new Date(stats.generatedAt).toLocaleString() : '—'}
          </span>
        </div>
        <div className="dash-header-actions">
          <button className="refresh-btn" onClick={fetchStats} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button className="logout-btn" onClick={() => setIsAuthenticated(false)}>
            Log Out
          </button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="dash-tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`dash-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div className="dash-content">
        {error && <div className="dash-error">{error}</div>}

        {/* ═══════ OVERVIEW TAB ═══════ */}
        {activeTab === 'Overview' && (
          <>
            <div className="metric-row">
              <div className="metric-card">
                <div className="metric-label">Total Signups</div>
                <div className="metric-value">{signupStats.total}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Purchase Attempts</div>
                <div className="metric-value">{purchaseStats.totalAttempts}</div>
                <div className="metric-sub">Last 24h: {purchaseStats.recentAttempts}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Page Clicks</div>
                <div className="metric-value">{stats.totalClicks}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Candy Attempts</div>
                <div className="metric-value">{purchaseStats.byProduct?.candy || 0}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Apparel Attempts</div>
                <div className="metric-value">{purchaseStats.byProduct?.apparel || 0}</div>
              </div>
            </div>

            {/* Quick breakdown pills */}
            <div className="dash-panel">
              <h2>Signup Breakdown</h2>
              <div className="stat-pills">
                <div className="stat-pill">
                  <span className="pill-value">{signupStats.candy}</span>
                  <span className="pill-label">Candy</span>
                </div>
                <div className="stat-pill">
                  <span className="pill-value">{signupStats.apparel}</span>
                  <span className="pill-label">Apparel</span>
                </div>
                <div className="stat-pill">
                  <span className="pill-value">{signupStats['candy & apparel']}</span>
                  <span className="pill-label">Both</span>
                </div>
              </div>
            </div>

            <div className="dash-panel">
              <h2>Page Views</h2>
              <div className="stat-pills">
                {Object.entries(stats.byPage || {}).map(([page, count]) => (
                  <div key={page} className="stat-pill">
                    <span className="pill-value">{count}</span>
                    <span className="pill-label">{PAGE_LABELS[page] || page}</span>
                  </div>
                ))}
                {Object.keys(stats.byPage || {}).length === 0 && (
                  <div className="stat-pill">
                    <span className="pill-value">0</span>
                    <span className="pill-label">No data</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ═══════ SIGNUPS TAB ═══════ */}
        {activeTab === 'Signups' && (
          <>
            <div className="metric-row">
              <div className="metric-card">
                <div className="metric-label">Total</div>
                <div className="metric-value">{signupStats.total}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Candy</div>
                <div className="metric-value">{signupStats.candy}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Apparel</div>
                <div className="metric-value">{signupStats.apparel}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Both</div>
                <div className="metric-value">{signupStats['candy & apparel']}</div>
              </div>
            </div>

            <div className="dash-panel">
              <h2>All Subscribers</h2>
              {signupList.length === 0 ? (
                <div className="dash-empty">No subscribers yet</div>
              ) : (
                <div className="subscriber-list">
                  {signupList.map((sub, i) => (
                    <div key={i} className="subscriber-item">
                      <span className="subscriber-email">{sub.email}</span>
                      <span className="subscriber-product">{sub.product}</span>
                      <span className="subscriber-date">
                        {new Date(sub.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* ═══════ CANDY TAB ═══════ */}
        {activeTab === 'Candy' && (
          <>
            <div className="metric-row">
              <div className="metric-card">
                <div className="metric-label">Total Candy Attempts</div>
                <div className="metric-value">{purchaseStats.byProduct?.candy || 0}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">60g Total</div>
                <div className="metric-value">{total60g}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">30g Total</div>
                <div className="metric-value">{total30g}</div>
              </div>
            </div>

            <div className="dash-panel">
              <h2>60g Pack Breakdown</h2>
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Pack Size</th>
                    <th>Attempts</th>
                    <th>% of 60g</th>
                  </tr>
                </thead>
                <tbody>
                  {['single', '3', '6', '12', '24'].map(pack => {
                    const count = purchaseStats.candyBreakdown?.['60g']?.[pack] || 0;
                    return (
                      <tr key={`60g-${pack}`}>
                        <td>{pack === 'single' ? 'Single Pack' : `${pack} Pack`}</td>
                        <td>{count}</td>
                        <td>{total60g > 0 ? ((count / total60g) * 100).toFixed(1) : 0}%</td>
                      </tr>
                    );
                  })}
                  <tr className="total-row">
                    <td><strong>Total</strong></td>
                    <td><strong>{total60g}</strong></td>
                    <td><strong>100%</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="dash-panel">
              <h2>30g Pack Breakdown</h2>
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Pack Size</th>
                    <th>Attempts</th>
                    <th>% of 30g</th>
                  </tr>
                </thead>
                <tbody>
                  {['single', '3', '6', '12', '24'].map(pack => {
                    const count = purchaseStats.candyBreakdown?.['30g']?.[pack] || 0;
                    return (
                      <tr key={`30g-${pack}`}>
                        <td>{pack === 'single' ? 'Single Pack' : `${pack} Pack`}</td>
                        <td>{count}</td>
                        <td>{total30g > 0 ? ((count / total30g) * 100).toFixed(1) : 0}%</td>
                      </tr>
                    );
                  })}
                  <tr className="total-row">
                    <td><strong>Total</strong></td>
                    <td><strong>{total30g}</strong></td>
                    <td><strong>100%</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ═══════ APPAREL TAB ═══════ */}
        {activeTab === 'Apparel' && (
          <>
            <div className="metric-row">
              <div className="metric-card">
                <div className="metric-label">Total Apparel Attempts</div>
                <div className="metric-value">{purchaseStats.byProduct?.apparel || 0}</div>
              </div>
              {Object.entries(purchaseStats.apparelByColor || {}).map(([color, count]) => (
                <div key={color} className="metric-card">
                  <div className="metric-label">{color}</div>
                  <div className="metric-value">{count}</div>
                </div>
              ))}
            </div>

            {purchaseStats.apparelBySize && Object.keys(purchaseStats.apparelBySize).length > 0 && (
              <div className="dash-panel">
                <h2>By Size</h2>
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Size</th>
                      <th>Attempts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(purchaseStats.apparelBySize).map(([size, count]) => (
                      <tr key={size}>
                        <td>{size}</td>
                        <td>{count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {purchaseStats.apparelBreakdown?.length > 0 && (
              <div className="dash-panel">
                <h2>Detailed Breakdown</h2>
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Color</th>
                      <th>Size</th>
                      <th>Attempts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseStats.apparelBreakdown.map((item, i) => (
                      <tr key={i}>
                        <td>{item.color}</td>
                        <td>{item.size}</td>
                        <td>{item.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ═══════ CLICKS TAB ═══════ */}
        {activeTab === 'Clicks' && (
          <>
            <div className="metric-row">
              <div className="metric-card">
                <div className="metric-label">Total Page Views</div>
                <div className="metric-value">{stats.totalClicks}</div>
              </div>
              {Object.entries(stats.byPage || {})
                .sort((a, b) => b[1] - a[1])
                .slice(0, 4)
                .map(([page, count]) => (
                  <div key={page} className="metric-card">
                    <div className="metric-label">{PAGE_LABELS[page] || page}</div>
                    <div className="metric-value">{count}</div>
                    <div className="metric-sub">
                      {stats.totalClicks > 0
                        ? ((count / stats.totalClicks) * 100).toFixed(1)
                        : 0}% of total
                    </div>
                  </div>
                ))}
            </div>

            <div className="dash-panel">
              <h2>All Page Views</h2>
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Page</th>
                    <th>Views</th>
                    <th>% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(stats.byPage || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([page, count]) => (
                      <tr key={page}>
                        <td>{PAGE_LABELS[page] || page}</td>
                        <td>{count}</td>
                        <td>
                          {stats.totalClicks > 0
                            ? ((count / stats.totalClicks) * 100).toFixed(1)
                            : 0}%
                        </td>
                      </tr>
                    ))}
                  <tr className="total-row">
                    <td><strong>Total</strong></td>
                    <td><strong>{stats.totalClicks}</strong></td>
                    <td><strong>100%</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="dash-footer">
        Data refreshes automatically every 30 seconds
      </div>
    </div>
  );
};

export default AdminDashboard;
